import React, { useState, useEffect, useRef, useContext } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FolderOpen as FolderIcon } from '@mui/icons-material';
import { DependencyInjectionContext } from '../../../contexts/dependency-injection-context';

interface FileSelectorProps {
    filePath: string | undefined;
    onFileChange: (filePath: string | undefined) => void;
    onBrowseClick: () => void;
    clientId: string;
    label?: string;
    fileType?: 'image' | 'video' | 'any';
    previewWidth?: number;
    previewHeight?: number;
}

export const FileSelector: React.FC<FileSelectorProps> = ({
    filePath,
    onFileChange,
    onBrowseClick,
    clientId,
    label = 'File',
    fileType = 'any',
    previewWidth = 100,
    previewHeight = 100
}) => {
    const { formsApi, applicationState } = useContext(DependencyInjectionContext);
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const [previewError, setPreviewError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const imageUrlsRef = useRef<Map<string, string>>(new Map());

    const loadFileWithAuth = async (path: string): Promise<string> => {
        const cacheKey = `${clientId}/${path}`;

        // Return cached URL if available
        if (imageUrlsRef.current.has(cacheKey)) {
            return imageUrlsRef.current.get(cacheKey)!;
        }

        try {
            const blob = await formsApi.fetchWelcomeBoardFile(path, clientId);
            const objectUrl = URL.createObjectURL(blob);

            // Cache the URL
            imageUrlsRef.current.set(cacheKey, objectUrl);

            return objectUrl;
        } catch (error) {
            console.error('Failed to load file:', error);
            throw error;
        }
    };

    useEffect(() => {
        let mounted = true;

        const loadPreview = async () => {
            if (!filePath) {
                setPreviewSrc(null);
                setPreviewError(false);
                return;
            }

            const cacheKey = `${clientId}/${filePath}`;

            // Check if already cached
            if (imageUrlsRef.current.has(cacheKey)) {
                if (mounted) {
                    setPreviewSrc(imageUrlsRef.current.get(cacheKey)!);
                    setPreviewError(false);
                }
                return;
            }

            setLoading(true);
            try {
                const url = await loadFileWithAuth(filePath);
                if (mounted) {
                    setPreviewSrc(url);
                    setPreviewError(false);
                }
            } catch (error) {
                if (mounted) {
                    setPreviewError(true);
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            mounted = false;
        };
    }, [filePath, clientId]);

    // Cleanup object URLs when component unmounts
    useEffect(() => {
        return () => {
            imageUrlsRef.current.forEach((url) => {
                URL.revokeObjectURL(url);
            });
        };
    }, []);

    const handleClear = () => {
        onFileChange(undefined);
        setPreviewSrc(null);
        setPreviewError(false);
    };

    const renderPreview = () => {
        if (!filePath) {
            return (
                <Box
                    sx={{
                        width: previewWidth,
                        height: previewHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: '8px',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                    }}
                >
                    No File
                </Box>
            );
        }

        if (loading) {
            return (
                <Box
                    sx={{
                        width: previewWidth,
                        height: previewHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        color: 'text.secondary',
                        fontSize: '0.875rem'
                    }}
                >
                    Loading...
                </Box>
            );
        }

        if (previewError) {
            return (
                <Box
                    sx={{
                        width: previewWidth,
                        height: previewHeight,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #ccc',
                        borderRadius: '8px',
                        color: 'error.main',
                        bgcolor: '#fff5f5',
                        fontSize: '0.875rem',
                        textAlign: 'center',
                        px: 1
                    }}
                >
                    Error Loading
                </Box>
            );
        }

        if (fileType === 'image' && previewSrc) {
            return (
                <img
                    src={previewSrc}
                    alt={label}
                    style={{
                        width: previewWidth,
                        height: previewHeight,
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                    }}
                />
            );
        }

        if (fileType === 'video' && previewSrc) {
            return (
                <video
                    src={previewSrc}
                    style={{
                        width: previewWidth,
                        height: previewHeight,
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                    }}
                    controls={false}
                    muted
                />
            );
        }

        // For 'any' type or unknown, show a generic file icon/placeholder
        return (
            <Box
                sx={{
                    width: previewWidth,
                    height: previewHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: 'text.secondary',
                    bgcolor: '#f5f5f5',
                    fontSize: '0.875rem'
                }}
            >
                File Selected
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {renderPreview()}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FolderIcon />}
                        onClick={onBrowseClick}
                    >
                        Browse
                    </Button>
                    {filePath && (
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            onClick={handleClear}
                        >
                            Clear
                        </Button>
                    )}
                </Box>
            </Box>
            {filePath && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    File: {filePath}
                </Typography>
            )}
        </Box>
    );
};
